import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { getState, patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { cloneDeep, isEqual, isNil } from 'lodash';
import { SortParams } from 'mbs-ui-kit/models/sort';
import { SmartSearchModel } from 'mbs-ui-kit/smart-search/models';
import { EnumHelper } from 'mbs-ui-kit/utils/enum-helper';
import { getPagingParams, getSortParams, PagingParams } from 'mbs-ui-shared@models';
import Computer, {
  AuthStatus,
  ComputerModeAdapter,
  ComputersApplications,
  ComputersHealthFilterType,
  ComputersMode
} from 'mbs-ui-shared@models/Computer';
import { ComputersStoreFilters } from 'mbs-ui-shared@models/ComputersStoreFilters';
import { SmartSearchTemplateName } from 'mbs-ui-shared@modules/smart-search-templates/models/smart-search-templates-models';
import { ComputersAbstractService } from 'mbs-ui-shared@services/computers.service';
import { isDataObsolete } from 'mbs-ui-shared@utils/store';
import { ComputersFiltersWrapper } from 'mbs-ui-shared@wrappers/computer.filters.wrapper';
import moment from 'moment';
import { EMPTY, forkJoin, pipe } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, switchMap } from 'rxjs/operators';

const OBSOLESCENCE_TIMEOUT = 5; // minutes
const CHUNK_SIZE = 50;

export interface ComputersPagingModeState {
  pages: Record<number, string[]>;
  loading: boolean;
  loaded: boolean;
  currentPageNumber: number;
  perPage: number | null;
  total: number;
  sort: SortParams;
  isErrorState: boolean;
  obsolescenceMark: number | null;
  changeCounterHash: number;
}

const initialPagingModeState: ComputersPagingModeState = {
  pages: {},
  loading: false,
  loaded: false,
  currentPageNumber: 1,
  perPage: null,
  total: 0,
  sort: { column: null, direction: null },
  isErrorState: false,
  obsolescenceMark: null,
  changeCounterHash: 0
};

export const initialComputersFilters: ComputersStoreFilters = {
  healthType: ComputersHealthFilterType.All,
  hidden: null,
  company: null,
  searchModel: {},
  authStatus: null
};

interface ComputersDataSection {
  loading: boolean;
  loaded: boolean;
  total: number;
  entities: string[];
  isErrorState: boolean;
  obsolescenceMark: number | null;
}

const initialDataSection: ComputersDataSection = {
  loading: false,
  loaded: false,
  total: 0,
  entities: [],
  isErrorState: false,
  obsolescenceMark: null
};

interface ComputersStoreState {
  entities: Record<string, Computer>;
  ids: string[];
  loading: boolean;
  bulkLoading: boolean;
  missedHids: string[];
  applicationsCount: ComputersApplications;
  applicationsCountInitiated: boolean;
  mode: ComputersMode;
  modeInitiated: boolean;
  pagingData: ComputersPagingModeState[];
  filtersData: ComputersStoreFilters[];
  filterInitiated: boolean;
  dataSection: ComputersDataSection;
}

function makeModeArray<T>(factory: () => T): T[] {
  return EnumHelper.EnumToArray(ComputersMode).map(factory);
}

const initialState: ComputersStoreState = {
  entities: {},
  ids: [],
  loading: false,
  bulkLoading: false,
  missedHids: [],
  applicationsCount: {},
  applicationsCountInitiated: false,
  mode: ComputersMode.Computers,
  modeInitiated: false,
  pagingData: makeModeArray(() => ({ ...initialPagingModeState })),
  filtersData: makeModeArray(() => ({ ...initialComputersFilters })),
  filterInitiated: false,
  dataSection: { ...initialDataSection }
};

function replaceEntitiesInDict(
  currentEntities: Record<string, Computer>,
  currentIds: string[],
  computers: Computer[]
): { entities: Record<string, Computer>; ids: string[] } {
  const newHids = computers.map((c) => c.hid);
  const newEntries = Object.fromEntries(computers.map((c) => [c.hid, c]));
  return {
    entities: { ...currentEntities, ...newEntries },
    ids: [...currentIds.filter((id) => !newHids.includes(id)), ...newHids]
  };
}

function clearPagingMode(mode: ComputersPagingModeState, clearAllFields = false): ComputersPagingModeState {
  return {
    ...mode,
    pages: {},
    total: initialPagingModeState.total,
    loaded: initialPagingModeState.loaded,
    isErrorState: initialPagingModeState.isErrorState,
    obsolescenceMark: initialPagingModeState.obsolescenceMark,
    ...(clearAllFields ? { currentPageNumber: initialPagingModeState.currentPageNumber, loading: initialPagingModeState.loading } : {})
  };
}

function filterIsEmpty(filters: ComputersStoreFilters): boolean {
  return isEqual(filters, initialComputersFilters);
}

function isFirstPageLoaded(paging: ComputersPagingModeState): boolean {
  return Object.keys(paging.pages).some((v) => parseInt(v, 10) === 1);
}

function dataHasEnoughForFirstPage(data: ComputersDataSection, paging: ComputersPagingModeState): boolean {
  return data.entities.length >= paging.perPage || (data.total < paging.perPage && data.entities.length === data.total);
}

function areEntitiesUsedInPaging(pagingPages: Record<number, string[]>, hids: string[]): boolean {
  return Object.values(pagingPages)
    .flat()
    .some((hid) => hids.includes(hid));
}

function areEntitiesUsedInData(dataEntities: string[], hids: string[]): boolean {
  return dataEntities.some((hid) => hids.includes(hid));
}

function isEntityObsolete(
  entity: Computer,
  pagingPages: Record<number, string[]>,
  pagingObsolescenceMark: number | null,
  dataEntities: string[],
  dataObsolescenceMark: number | null
): boolean {
  if (!entity) return false;
  if (entity.obsolescenceMark) return isDataObsolete(entity.obsolescenceMark, OBSOLESCENCE_TIMEOUT);
  const pagingMark = areEntitiesUsedInPaging(pagingPages, [entity.hid]) ? pagingObsolescenceMark : null;
  const dataMark = areEntitiesUsedInData(dataEntities, [entity.hid]) ? dataObsolescenceMark : null;
  return isDataObsolete(pagingMark, OBSOLESCENCE_TIMEOUT) || isDataObsolete(dataMark, OBSOLESCENCE_TIMEOUT);
}

function computeChangeCounterHash(currentHash: number, pagingPages: Record<number, string[]>, computers?: Computer[]): number {
  const needToChange = computers
    ? areEntitiesUsedInPaging(
        pagingPages,
        computers.map((c) => c?.hid)
      )
    : true;

  return needToChange ? Math.floor(Math.random() * 1000000) : currentHash;
}

function splitIntoChunks(arr: string[], size = CHUNK_SIZE): string[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, (i + 1) * size));
}

export const ComputersStore = signalStore(
  { providedIn: 'root' },
  withDevtools('ComputersStore'),
  withState<ComputersStoreState>(initialState),

  withComputed((store) => ({
    all: computed(() =>
      store
        .ids()
        .map((id) => store.entities()[id])
        .filter(Boolean)
    ),

    currentPageNumber: computed(() => store.pagingData()[store.mode()].currentPageNumber),
    pageNumbers: computed(() => Object.keys(store.pagingData()[store.mode()].pages).map((value) => parseInt(value, 10))),
    currentPageIds: computed(() => {
      const mode = store.mode();
      const paging = store.pagingData()[mode];
      return (paging.currentPageNumber && paging.pages[paging.currentPageNumber]) || [];
    }),
    currentPageData: computed(() => {
      const mode = store.mode();
      const paging = store.pagingData()[mode];
      const ids = (paging.currentPageNumber && paging.pages[paging.currentPageNumber]) || [];
      const entries = store.entities();
      return ids.map((id) => entries[id]).filter(Boolean);
    }),
    perPage: computed(() => store.pagingData()[store.mode()].perPage),
    totalItems: computed(() => store.pagingData()[store.mode()].total),
    sortSettings: computed(() => store.pagingData()[store.mode()].sort),
    pagingLoading: computed(() => store.pagingData()[store.mode()].loading),
    pagingLoaded: computed(() => store.pagingData()[store.mode()].loaded),
    pagingHasError: computed(() => store.pagingData()[store.mode()].isErrorState || false),
    changeCounterHash: computed(() => store.pagingData()[store.mode()].changeCounterHash),

    allFilters: computed(() => store.filtersData()[store.mode()]),
    filterHealthType: computed(() => store.filtersData()[store.mode()].healthType),
    filterAuthStatus: computed(() => store.filtersData()[store.mode()].authStatus),
    filterHidden: computed(() => store.filtersData()[store.mode()].hidden),
    filterCompany: computed(() => store.filtersData()[store.mode()].company),
    filterSearchModel: computed(() => store.filtersData()[store.mode()].searchModel),
    filterHealth: computed(() => ({
      healthType: store.filtersData()[store.mode()].healthType,
      authStatus: store.filtersData()[store.mode()].authStatus
    })),
    hasFilters: computed(() => {
      const filters = store.filtersData()[store.mode()];
      return (
        filters.healthType !== ComputersHealthFilterType.All ||
        filters.hidden ||
        filters.company ||
        isNil(filters.searchModel) ||
        !!Object.keys(filters.searchModel).length
      );
    }),

    dataLoading: computed(() => store.dataSection().loading),
    dataLoaded: computed(() => store.dataSection().loaded),
    dataTotal: computed(() => store.dataSection().total),
    dataCount: computed(() => store.dataSection().entities.length),
    dataHasError: computed(() => store.dataSection().isErrorState || false),
    allDataLoaded: computed(() => store.dataSection().loaded && store.dataSection().total <= store.dataSection().entities.length),
    computersData: computed(() =>
      store
        .dataSection()
        .entities.map((hid) => store.entities()[hid])
        .filter(Boolean)
    ),
    computersNotFound: computed(() => store.ids().length === 0 && store.pagingData()[store.mode()].loaded),
    dataEntityLoading: computed(() => store.loading() || store.dataSection().loading),
    pagingEntityLoading: computed(() => store.loading() || store.pagingData()[store.mode()].loading),

    requestParams: computed(() => {
      const mode = store.mode();
      return {
        paging: {
          currentPageNumber: store.pagingData()[mode].currentPageNumber,
          perPage: store.pagingData()[mode].perPage
        } as PagingParams,
        sorting: store.pagingData()[mode].sort,
        filters: store.filtersData()[mode]
      };
    })
  })),

  withMethods((store) => {
    const computersService = inject(ComputersAbstractService);
    const filtersWrapper = inject(ComputersFiltersWrapper);

    const fetchPage = rxMethod<{
      pageNumber: number;
      sorting: SortParams;
      filters: ComputersStoreFilters;
      perPage: number;
    }>(
      pipe(
        switchMap(({ pageNumber, sorting, filters, perPage }) => {
          const paging: PagingParams = { currentPageNumber: pageNumber, perPage };
          return filtersWrapper.getComputersHttpParams(filters, getSortParams(sorting), getPagingParams(paging)).pipe(
            switchMap((params) => computersService.getComputers(params)),
            tapResponse({
              next: (result) => {
                const { entities, ids, mode, pagingData } = getState(store);
                const newPaging = cloneDeep(pagingData);
                const paging = newPaging[mode];
                paging.pages[pageNumber] = result.data.map((computer) => computer.hid);
                paging.total = result.total;
                paging.loading = false;
                paging.loaded = true;
                paging.isErrorState = false;
                paging.obsolescenceMark = paging.obsolescenceMark ?? moment().valueOf();
                paging.changeCounterHash = computeChangeCounterHash(paging.changeCounterHash, paging.pages, result.data);
                patchState(store, { ...replaceEntitiesInDict(entities, ids, result.data), pagingData: newPaging });
              },
              error: () => {
                const { mode, pagingData } = getState(store);
                const newPaging = cloneDeep(pagingData);
                newPaging[mode].loading = false;
                newPaging[mode].isErrorState = true;
                patchState(store, { pagingData: newPaging });
              }
            })
          );
        })
      )
    );

    const fetchData = rxMethod<{ isFullRefresh?: boolean; allowLoadMore?: boolean; limit?: number; offset: number }>(
      pipe(
        mergeMap(({ offset, limit, allowLoadMore, isFullRefresh }) =>
          computersService.getComputers({ offset, ...(limit > 0 ? { limit } : {}) }).pipe(
            tapResponse({
              next: (result) => {
                const { entities, ids, mode, pagingData } = getState(store);
                const newPaging = cloneDeep(pagingData);
                newPaging[mode].changeCounterHash = computeChangeCounterHash(
                  newPaging[mode].changeCounterHash,
                  newPaging[mode].pages,
                  result.data
                );
                const newData: ComputersDataSection = {
                  ...getState(store).dataSection,
                  total: result.total,
                  loaded: true,
                  loading: false,
                  isErrorState: false,
                  entities:
                    isFullRefresh || !allowLoadMore
                      ? result.data.map((computer) => computer.hid)
                      : [...getState(store).dataSection.entities, ...result.data.map((computer) => computer.hid)],
                  obsolescenceMark: getState(store).dataSection.obsolescenceMark ?? moment().valueOf()
                };
                patchState(store, { ...replaceEntitiesInDict(entities, ids, result.data), dataSection: newData, pagingData: newPaging });
              },
              error: () => {
                patchState(store, { dataSection: { ...getState(store).dataSection, loading: false, isErrorState: true } });
              }
            })
          )
        )
      )
    );

    const fetchApplicationsCount = rxMethod<void>(
      pipe(
        exhaustMap(() =>
          computersService.getComputersApplicationsCount().pipe(
            tapResponse({
              next: (count) => patchState(store, { applicationsCount: count, applicationsCountInitiated: true }),
              error: () => patchState(store, { applicationsCount: {}, applicationsCountInitiated: true })
            })
          )
        )
      )
    );

    const fetchComputerByHid = rxMethod<{ hid: string }>(
      pipe(
        mergeMap(({ hid }) =>
          computersService.getComputerByHid(hid).pipe(
            tapResponse({
              next: (result) => {
                const { entities, ids, mode, pagingData } = getState(store);
                const newPaging = cloneDeep(pagingData);
                newPaging[mode].changeCounterHash = computeChangeCounterHash(newPaging[mode].changeCounterHash, newPaging[mode].pages, [
                  result
                ]);
                patchState(store, {
                  ...replaceEntitiesInDict(entities, ids, [{ ...result, obsolescenceMark: moment().valueOf() }]),
                  loading: false,
                  pagingData: newPaging
                });
              },
              error: () => patchState(store, { loading: false })
            })
          )
        )
      )
    );

    return {
      entityByHid: (hid: string) => computed(() => store.entities()[hid]),
      entitiesByHids: (hids: string[]) => computed(() => hids.map((hid) => store.entities()[hid]).filter(Boolean)),
      obsoleteByHids: (hids: string[]) => computed(() => hids.filter((hid) => !store.entities()[hid] || store.entities()[hid]?.obsolete)),
      missedByHid: (hid: string) => computed(() => store.missedHids().includes(hid)),

      setComputers(computers: Computer[]): void {
        const { entities, ids, mode, pagingData } = getState(store);
        const newPaging = cloneDeep(pagingData);
        newPaging[mode].changeCounterHash = computeChangeCounterHash(newPaging[mode].changeCounterHash, newPaging[mode].pages, computers);
        patchState(store, {
          ...replaceEntitiesInDict(
            entities,
            ids,
            computers.map((computer) => ({ ...computer, obsolescenceMark: moment().valueOf() }))
          ),
          loading: false,
          pagingData: newPaging
        });
      },

      loadComputerByHid(params: { hid: string; force?: boolean; quiet?: boolean }): void {
        const { entities } = getState(store);
        const entity = entities[params.hid];
        const paging = store.pagingData()[store.mode()];
        const dataSection = store.dataSection();
        const obsolete = entity
          ? isEntityObsolete(entity, paging.pages, paging.obsolescenceMark, dataSection.entities, dataSection.obsolescenceMark)
          : false;

        if (!params.hid || (entity && !params.force && !obsolete)) return;

        !params.quiet && patchState(store, { loading: true });
        fetchComputerByHid({ hid: params.hid });
      },

      loadComputersByHids(params: {
        hids: string[];
        includes?: string[];
        force?: boolean;
        loadDeleted?: boolean;
        loadHidden?: boolean;
      }): void {
        const { entities } = getState(store);
        const paging = store.pagingData()[store.mode()];
        const dataSection = store.dataSection();
        const selectedHids = params.force
          ? params.hids
          : params.hids.filter((hid) => {
              const entity = entities[hid];
              const obsolete = entity
                ? isEntityObsolete(entity, paging.pages, paging.obsolescenceMark, dataSection.entities, dataSection.obsolescenceMark)
                : false;

              return obsolete || !entity;
            });

        if (!selectedHids.length) return;

        patchState(store, { bulkLoading: true });

        forkJoin(
          splitIntoChunks(selectedHids).map((chunk) =>
            computersService.getComputersByHids(chunk, params.includes, params.loadDeleted, params.loadHidden)
          )
        )
          .pipe(
            map((responses) => responses.reduce<Computer[]>((acc, next) => (acc ?? []).concat(next), [])),
            catchError(() => {
              patchState(store, { bulkLoading: false });
              return EMPTY;
            })
          )
          .subscribe((computers) => {
            const { entities: currentEntities, ids, mode, pagingData } = getState(store);
            const newPaging = cloneDeep(pagingData);
            const receivedHids = computers.map((c) => c.hid);
            newPaging[mode].changeCounterHash = computeChangeCounterHash(
              newPaging[mode].changeCounterHash,
              newPaging[mode].pages,
              computers
            );

            patchState(store, {
              ...replaceEntitiesInDict(
                currentEntities,
                ids,
                computers.map((computer) => ({ ...computer, obsolescenceMark: moment().valueOf() }))
              ),
              bulkLoading: false,
              missedHids: [
                ...store.missedHids(),
                ...selectedHids.filter((hid) => !store.missedHids().includes(hid) && !receivedHids.includes(hid))
              ],
              pagingData: newPaging
            });
          });
      },

      loadApplicationsCount(params: { force?: boolean } = {}): void {
        if (!params.force && store.applicationsCountInitiated()) return;
        fetchApplicationsCount();
      },

      setMode(params: { mode: ComputersMode }): void {
        const { mode: oldMode, modeInitiated, filtersData, pagingData } = getState(store);
        const newMode = params.mode;

        if (!Object.values(ComputersMode).includes(newMode) || (oldMode === newMode && modeInitiated)) return;

        const newFiltersData = cloneDeep(filtersData);
        const lastFilter = cloneDeep(newFiltersData[newMode]);

        newFiltersData[newMode] = {
          healthType: newFiltersData[oldMode].healthType,
          authStatus: newFiltersData[oldMode].authStatus,
          hidden: newFiltersData[oldMode].hidden,
          company: newFiltersData[oldMode].company,
          searchModel: cloneDeep(newFiltersData[oldMode].searchModel)
        };

        if (newFiltersData[newMode].searchModel[SmartSearchTemplateName.ProductInstalled]) {
          delete newFiltersData[newMode].searchModel[SmartSearchTemplateName.ProductInstalled];
        }

        const modeAgentTypes = ComputerModeAdapter.filter((modeAgentType) => !!modeAgentType.agentType);
        if (modeAgentTypes.map((value) => value.mode).includes(newMode)) {
          newFiltersData[newMode].searchModel[SmartSearchTemplateName.ProductInstalled] = [
            { condition: undefined, value: modeAgentTypes.find((v) => v.mode === newMode).displayAgentType }
          ];
        }

        let newPagingData = pagingData;
        if (!isEqual(lastFilter, newFiltersData[newMode])) {
          newPagingData = cloneDeep(pagingData);
          newPagingData[newMode] = clearPagingMode(newPagingData[newMode], true);
        }

        patchState(store, { mode: newMode, modeInitiated: true, filtersData: newFiltersData, pagingData: newPagingData });
      },

      loadPage(action: { pageNumber?: number; perPage?: number; isFullRefresh?: boolean } = {}): void {
        const { mode, filtersData, dataSection } = getState(store);
        const newPagingData = cloneDeep(store.pagingData());
        const paging = newPagingData[mode];

        const isDataOutdated = isDataObsolete(dataSection.obsolescenceMark, OBSOLESCENCE_TIMEOUT);
        const isPagingOutdated = isDataObsolete(paging.obsolescenceMark, OBSOLESCENCE_TIMEOUT);

        paging.currentPageNumber = action.pageNumber ?? paging.currentPageNumber;

        let shouldClear = isPagingOutdated || !!action.isFullRefresh;

        if (action.perPage && paging.perPage !== action.perPage) {
          paging.perPage = action.perPage;
          paging.currentPageNumber = initialPagingModeState.currentPageNumber;
          shouldClear = true;
        }

        if (shouldClear) {
          const cleared = clearPagingMode(paging);
          Object.assign(paging, cleared);
        }

        // Cross-copy data → paging if applicable
        if (
          !action.isFullRefresh &&
          !isDataOutdated &&
          dataSection.loaded &&
          filterIsEmpty(filtersData[mode]) &&
          paging.currentPageNumber === 1 &&
          !isFirstPageLoaded(paging) &&
          dataHasEnoughForFirstPage(dataSection, paging)
        ) {
          paging.pages[1] = dataSection.entities.slice(0, paging.perPage);
          paging.total = dataSection.total;
          paging.loaded = true;
          paging.isErrorState = false;
          paging.obsolescenceMark = paging.obsolescenceMark
            ? Math.min(paging.obsolescenceMark, dataSection.obsolescenceMark)
            : dataSection.obsolescenceMark;
        }

        paging.loading = !(paging.loaded && !!paging.pages[paging.currentPageNumber]) && (!paging.isErrorState || !!action.isFullRefresh);

        patchState(store, { pagingData: newPagingData });

        const pageNumbers = Object.keys(paging.pages).map((v) => parseInt(v, 10));
        const shouldFetch =
          !pageNumbers.some((page) => page === paging.currentPageNumber) && (!paging.isErrorState || !!action.isFullRefresh);

        if (shouldFetch) {
          fetchPage({ pageNumber: paging.currentPageNumber, sorting: paging.sort, filters: filtersData[mode], perPage: paging.perPage });
        }
      },

      clearPages(): void {
        const { mode, pagingData } = getState(store);
        const newPaging = cloneDeep(pagingData);
        newPaging[mode] = clearPagingMode(newPaging[mode], true);
        patchState(store, { pagingData: newPaging });
      },

      setPageNumber(params: { pageNumber: number }): void {
        this.loadPage({ pageNumber: params.pageNumber });
      },

      setPerPageSetting(params: { perPage: number }): void {
        this.loadPage({ perPage: params.perPage });
      },

      setSortSettings(params: { sort: SortParams }): void {
        const { mode, pagingData } = getState(store);
        if (isEqual(pagingData[mode].sort, params.sort)) return;
        const newPaging = cloneDeep(pagingData);
        newPaging[mode].sort = cloneDeep(params.sort);
        patchState(store, { pagingData: newPaging });
      },

      setFilterHidden(params: { hidden: boolean }): void {
        const { mode, filtersData } = getState(store);
        if (filtersData[mode].hidden === params.hidden) return;
        const newFilters = cloneDeep(filtersData);
        newFilters[mode].hidden = params.hidden;
        patchState(store, { filtersData: newFilters });
      },

      setFilterSearch(params: { searchModel: SmartSearchModel }): void {
        const { mode, filtersData } = getState(store);
        if (isEqual(filtersData[mode].searchModel, params.searchModel)) return;
        const newFilters = cloneDeep(filtersData);
        newFilters[mode].searchModel = cloneDeep(params.searchModel);
        patchState(store, { filtersData: newFilters });
      },

      setFilterCompany(params: { company: string }): void {
        const { mode, filtersData } = getState(store);
        if (filtersData[mode].company === params.company) return;
        const newFilters = cloneDeep(filtersData);
        newFilters[mode].company = params.company;
        patchState(store, { filtersData: newFilters });
      },

      setFilterHealth(params: { healthType: ComputersHealthFilterType }): void {
        const { mode, filtersData } = getState(store);
        const authStatus = params.healthType === ComputersHealthFilterType.Pending ? AuthStatus.Pending : null;
        if (filtersData[mode].healthType === params.healthType && filtersData[mode].authStatus === authStatus) return;
        const newFilters = cloneDeep(filtersData);
        newFilters[mode].healthType = params.healthType;
        newFilters[mode].authStatus = authStatus;
        patchState(store, { filtersData: newFilters });
      },

      setAllFilters(params: {
        searchModel?: SmartSearchModel;
        hidden?: boolean;
        healthType?: ComputersHealthFilterType;
        company?: string;
        apply?: boolean;
      }): void {
        const { mode, filtersData, filterInitiated } = getState(store);
        const current = filtersData[mode];
        const authStatus = params.healthType === ComputersHealthFilterType.Pending ? AuthStatus.Pending : null;

        const searchChanged = (!!params.searchModel || params.apply) && !isEqual(current.searchModel, params.searchModel);
        const healthChanged =
          (!!params.healthType || params.apply) && (current.healthType !== params.healthType || current.authStatus !== authStatus);
        const hiddenChanged = !current.hidden && !!params.hidden;
        const companyChanged = (!!params.company || params.apply) && current.company !== params.company;
        const changed = searchChanged || healthChanged || hiddenChanged || companyChanged;

        if (!changed) {
          if (!filterInitiated) patchState(store, { filterInitiated: true });
          return;
        }

        const newFilters = cloneDeep(filtersData);
        if (searchChanged) newFilters[mode].searchModel = cloneDeep(params.searchModel ?? {});
        if (healthChanged) {
          newFilters[mode].healthType = params.healthType ?? ComputersHealthFilterType.All;
          newFilters[mode].authStatus = authStatus;
        }
        if (hiddenChanged) newFilters[mode].hidden = params.hidden;
        if (companyChanged) newFilters[mode].company = params.company;

        patchState(store, { filtersData: newFilters, filterInitiated: true });
      },

      setLoading(params: { loading: boolean }): void {
        patchState(store, { loading: params.loading });
      },

      loadData(isFullRefresh = false, allowLoadMore = false, limit: number = null): void {
        const { dataSection, pagingData, mode, filtersData } = getState(store);
        const isDataOutdated = isDataObsolete(dataSection.obsolescenceMark, OBSOLESCENCE_TIMEOUT);
        const isPagingOutdated = isDataObsolete(pagingData[ComputersMode.Computers].obsolescenceMark, OBSOLESCENCE_TIMEOUT);
        let allLoaded = dataSection.total <= (dataSection.entities || []).length;

        if (!isDataOutdated && !isFullRefresh && dataSection.loaded && allLoaded) return;

        let newData = { ...dataSection };

        if (isFullRefresh || isDataOutdated) {
          newData = { ...initialDataSection };
        }

        // Cross-copy paging → data if applicable
        if (
          !isFullRefresh &&
          !isPagingOutdated &&
          !newData.loaded &&
          pagingData[ComputersMode.Computers].loaded &&
          filterIsEmpty(filtersData[ComputersMode.Computers]) &&
          isFirstPageLoaded(pagingData[ComputersMode.Computers])
        ) {
          newData.entities = [...pagingData[ComputersMode.Computers].pages[1]];
          newData.total = pagingData[ComputersMode.Computers].total;
          newData.loaded = true;
          newData.isErrorState = false;
          newData.obsolescenceMark = pagingData[ComputersMode.Computers].obsolescenceMark;
        }

        newData.loading =
          (!newData.loaded || (newData.total > newData.entities.length && allowLoadMore)) && (!newData.isErrorState || isFullRefresh);

        patchState(store, { dataSection: newData });

        // Check if fetch is needed
        allLoaded = newData.loaded && newData.total <= (newData.entities || []).length;
        if ((allowLoadMore || !newData.entities.length) && !allLoaded && (!newData.isErrorState || isFullRefresh)) {
          fetchData({ isFullRefresh, allowLoadMore, limit, offset: newData.entities.length });
        }
      },

      clearData(): void {
        const { dataSection } = getState(store);
        patchState(store, {
          dataSection: {
            ...dataSection,
            entities: [],
            total: initialDataSection.total,
            loaded: initialDataSection.loaded,
            isErrorState: initialDataSection.isErrorState,
            obsolescenceMark: initialDataSection.obsolescenceMark,
            loading: initialDataSection.loading
          }
        });
      }
    };
  })
);

export type ComputersStoreType = InstanceType<typeof ComputersStore>;
